import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { debounce } from "lodash-es";
import { Control, Controller, useFormContext, useWatch } from "react-hook-form";

import { JsonSchemaEditor } from "src/aiNode/editorComponents/JsonSchemaEditor";
import {
  AI_NODE_V2_INSIGHTS,
  AiNodeJsonSchemaBE,
  AiNodeJsonSchemaUI,
  AiNodeSelectableSchemaTypes,
  AiNodeV2Form,
  AiNodeV2Insight,
  AiNodeV2MetaFixed,
} from "src/aiNode/types";
import {
  convertAiNodeJsonSchemaBETtoUIT,
  convertAiNodeV2FormToBE,
} from "src/aiNode/utils";
import { FieldErrors } from "src/api/types";
import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { ExternalLink } from "src/base-components/ExternalLink";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { PillToggle } from "src/base-components/PillToggle";
import { Switch } from "src/base-components/Switch";
import { SCHEMA_TYPE_ICONS } from "src/base-components/TypeIcons";
import { AiNodeV2ResponseFormatConfigTypeEnum } from "src/clients/flow-api";
import { DOCS_AI_NODE } from "src/constants/ExternalLinks";
import { Button } from "src/design-system/Button";
import { Callout } from "src/design-system/Callout";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { JsonSchema } from "src/jsonSchema/types";
import { SchemaOptions } from "src/router/SearchParams";
import { defaultSchema, SchemaConverter } from "src/schema/schemaMappingUtils";
import { deepCopyWithoutKeys } from "src/utils/deepCopyWithoutKeys";

type ResponseMappingProps = {
  runFieldErrors: FieldErrors | undefined;
  // Schema-scoped run errors (e.g. a variable-sourced enum resolving to a
  // non-list value). Node-scoped rather than per-row, so they're surfaced as a
  // section-level callout above the schema editor.
  responseSchemaErrors?: string[];
  immutable: boolean;
  onUpdate?: (data: { newData: AiNodeV2MetaFixed }) => void;
};

const INSIGHT_DEFINITIONS: Record<
  AiNodeV2Insight,
  {
    displayName: string;
    description: string;
  }
> = {
  input_tokens_used: {
    displayName: "Input tokens used",
    description: "The number of input tokens used by the model",
  },
  output_tokens_used: {
    displayName: "Output tokens used",
    description: "The number of output tokens used by the model",
  },
  total_cost: {
    displayName: "Total cost",
    description: "The total cost of the model response",
  },
  inference_latency: {
    displayName: "Inference latency",
    description:
      "The time in milliseconds it took for the model to return the response",
  },
};

const SCHEMA_DEFAULT_TYPE_OPTIONS = Object.entries(
  AiNodeSelectableSchemaTypes,
).map(([key, value]) => ({
  key: key,
  value: value,
  icon: SCHEMA_TYPE_ICONS[value],
}));

const OutputLocationCard: React.FC<{
  immutable: boolean;
  control: Control<AiNodeV2Form>;
  error?: string;
  fieldPath: "response.default" | `insights.${AiNodeV2Insight}`;
  placeholder: string;
}> = ({ immutable, control, error, fieldPath, placeholder }) => {
  const { getValues } = useFormContext<AiNodeV2Form>();
  const fieldId = getValues(`${fieldPath}.id`);
  const isSearchHighlighted = useIsFieldHighlighted(`${fieldId}:field`);

  return (
    <Card className="flex w-full flex-row items-center gap-x-3">
      <div className="flex font-inter-medium-12px text-gray-800">
        Output location
      </div>
      <Controller
        control={control}
        name={`${fieldPath}.mapped_to`}
        render={({ field: controlledField }) => (
          <AutocompleteCodeInput
            dataLoc={`ai-node-${fieldPath}.mappedTo`}
            disabled={immutable}
            error={error}
            isSearchHighlighted={isSearchHighlighted}
            placeholder={placeholder}
            prefix="data."
            value={controlledField.value}
            onChange={controlledField.onChange}
          />
        )}
      />
    </Card>
  );
};

interface TextOutputProps {
  runFieldErrors: FieldErrors | undefined;
  immutable: boolean;
}

const TextOutputField: React.FC<TextOutputProps> = ({
  runFieldErrors,
  immutable,
}) => {
  const formProps = useFormContext<AiNodeV2Form>();

  const fieldPath = `response.default`;

  const error = runFieldErrors?.[formProps.getValues(`${fieldPath}.id`)];

  return (
    <OutputLocationCard
      control={formProps.control}
      error={error}
      fieldPath={fieldPath}
      immutable={immutable}
      placeholder="ai_response"
    />
  );
};

const TextOutputMapping: React.FC<TextOutputProps> = ({
  runFieldErrors,
  immutable,
}) => {
  return (
    <div className="flex flex-col gap-y-4">
      <div className="font-inter-normal-12px text-gray-500">
        Get the AI's response as unstructured text. Choose this when you need
        more flexible, human-readable outputs.
      </div>
      <TextOutputField immutable={immutable} runFieldErrors={runFieldErrors} />
    </div>
  );
};

const Insights = ({
  immutable,
  runFieldErrors,
}: {
  immutable: boolean;
  runFieldErrors: FieldErrors | undefined;
}) => {
  const { control, getValues } = useFormContext<AiNodeV2Form>();
  const insights = getValues("insights");
  return (
    <div className="flex flex-col gap-y-4">
      {AI_NODE_V2_INSIGHTS.map((insightName) => {
        const insightDefinition = INSIGHT_DEFINITIONS[insightName];
        const insightId = insights[insightName]?.id;
        return (
          <div
            key={insightName}
            className="space-y-2"
            data-loc={`ai-node-insights-${insightName}-container`}
          >
            <div className="items-top flex justify-between gap-x-2">
              <div>
                <h3 className="font-inter-semibold-13px text-gray-800">
                  {insightDefinition.displayName}
                </h3>
                <span className="font-inter-normal-12px text-gray-500">
                  {insightDefinition.description}
                </span>
              </div>
              <div>
                <Controller
                  control={control}
                  name={`insights.${insightName}.active`}
                  render={({ field: controlledField }) => (
                    <Switch
                      dataLoc={`ai-node-insights-${insightName}-toggle`}
                      disabled={immutable}
                      enabled={controlledField.value}
                      onChange={() => {
                        controlledField.onChange(!controlledField.value);
                      }}
                    />
                  )}
                />
              </div>
            </div>
            {getValues(`insights.${insightName}.active`) && (
              <OutputLocationCard
                control={control}
                error={runFieldErrors?.[insightId]}
                fieldPath={`insights.${insightName}`}
                immutable={immutable}
                placeholder={insightName}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export const ResponseMappingV2: React.FC<ResponseMappingProps> = ({
  runFieldErrors,
  responseSchemaErrors,
  immutable: externalImmutable,
  onUpdate,
}) => {
  const formProps = useFormContext<AiNodeV2Form>();
  const formLocationResponseType = "inference.response_format.type" as const;
  const formLocationJsonSchema =
    "inference.response_format.json_schema" as const;
  const formLocationRows =
    "inference.response_format.json_schema.properties" as const;

  const responseType = formProps.getValues(formLocationResponseType);
  const [selectedType, setSelectedType] =
    useState<AiNodeV2ResponseFormatConfigTypeEnum>(responseType);
  const uiSchema = formProps.getValues(formLocationJsonSchema);

  const { open: openWindow, updateData: updateWindowData } =
    useFloatingWindowsActions();
  const sidePaneTargetRef = useRef<HTMLDivElement | null>(null);

  const [isLocked, setIsLocked] = useState(false);
  const [isJsonSchemaWindowOpen, setIsJsonSchemaWindowOpen] = useState(false);
  const immutable = isLocked || externalImmutable;

  const fields = useWatch({
    control: formProps.control,
    name: formLocationRows,
  });
  const isEmpty = !fields || fields.length === 0;

  const updateResponseType = (type: AiNodeV2ResponseFormatConfigTypeEnum) => {
    if (!immutable) {
      setSelectedType(type);
      formProps.setValue(formLocationResponseType, type);
    }
  };

  const resetFormSchemaAndReinitializeFieldArrays = (
    schema: AiNodeJsonSchemaUI,
  ) => {
    // Note: We need to do a full reset here to tear down and re-initialize all downstream fieldArrays
    // and subscriptions. These keep their own state and don't get automatically synchronized.
    formProps.setValue(formLocationJsonSchema, schema);
    formProps.reset({
      ...formProps.getValues(),
    });
  };

  const handleFieldsCleared = () => {
    if (immutable) {
      return;
    }
    const defaultUISchema = SchemaConverter.beToUI(
      defaultSchema,
      SchemaOptions.Output,
    ) as AiNodeJsonSchemaUI;

    resetFormSchemaAndReinitializeFieldArrays(defaultUISchema);
  };

  const windowData = {
    title: externalImmutable ? "JSON schema" : "Generate from JSON Schema",
    subtitle: externalImmutable ? (
      <span>This schema is read-only because the flow is published.</span>
    ) : (
      <>
        <span>
          Paste a JSON schema below to generate your structured output schema.
        </span>
        &nbsp;
        <ExternalLink href={DOCS_AI_NODE}>Learn more</ExternalLink>
      </>
    ),
    infoText: externalImmutable
      ? undefined
      : "Add a description for every property in your JSON schema, especially nested ones, to help the AI understand each field and improve accuracy.",
    immutable: externalImmutable,
    onSave: (schema: string) => {
      // Convert to UI schema
      // Note: bubble up any errors here, should have been caught earlier during validation
      const parsedSchema = JSON.parse(schema);
      const newUISchema = convertAiNodeJsonSchemaBETtoUIT(
        parsedSchema as AiNodeJsonSchemaBE,
      );

      // Update the form state
      resetFormSchemaAndReinitializeFieldArrays(newUISchema);

      // Manually call the update handler because RHF onChange won't trigger when resetting the form
      if (onUpdate != null) {
        onUpdate({
          newData: convertAiNodeV2FormToBE({ ...formProps.getValues() }),
        });
      }

      setIsLocked(false);
      setIsJsonSchemaWindowOpen(false);
    },
    onDiscard: () => {
      setIsLocked(false);
      setIsJsonSchemaWindowOpen(false);
    },
    onChange: (_: string, isDirty: boolean) => {
      setIsLocked(isDirty);
    },
  };

  const jsonSchemaEditorWindowId = "json-schema-editor";

  const refreshJsonSchemaEditor = useCallback(() => {
    const currentUISchema =
      formProps.getValues().inference.response_format.json_schema;

    let updatedRawJson;
    if (currentUISchema) {
      let backendSchema = SchemaConverter.uiToBE(
        currentUISchema,
        SchemaOptions.Output,
      );
      // Make sure outputId is omitted from the generated JSON
      backendSchema = deepCopyWithoutKeys(backendSchema, ["outputId"]);
      updatedRawJson = JSON.stringify(backendSchema, null, 2);
    }

    // Update the window data with the new schema
    updateWindowData(jsonSchemaEditorWindowId, {
      ...windowData,
      initialSchema: updatedRawJson,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowData, updateWindowData]);

  const debouncedRefreshJsonSchemaEditor = useMemo(
    () => debounce(refreshJsonSchemaEditor, 200),
    [refreshJsonSchemaEditor],
  );

  useEffect(() => {
    if (!isJsonSchemaWindowOpen) {
      return;
    }
    debouncedRefreshJsonSchemaEditor();
  }, [fields, isJsonSchemaWindowOpen, debouncedRefreshJsonSchemaEditor]);

  const handleOpenJsonSchemaEditor = () => {
    let initialRawJson;

    const currentUISchema =
      formProps.getValues().inference.response_format.json_schema;

    if (currentUISchema) {
      let backendSchema = SchemaConverter.uiToBE(
        currentUISchema,
        SchemaOptions.Output,
      );
      // A little ugly... make sure outputId is omitted from the generated JSON
      backendSchema = deepCopyWithoutKeys(backendSchema, ["outputId"]);

      initialRawJson = JSON.stringify(backendSchema, null, 2);
    }

    openWindow(
      {
        id: jsonSchemaEditorWindowId,
        type: "json-schema-editor",
        target: sidePaneTargetRef,
        positionOptions: {
          placement: "left",
        },
      },
      {
        ...windowData,
        initialSchema: initialRawJson,
      },
    );
    setIsJsonSchemaWindowOpen(true);
  };

  useLayoutEffect(() => {
    const nodeEditor = document.querySelector("[data-loc=node-editor-pane]");
    sidePaneTargetRef.current = nodeEditor as HTMLDivElement;
  }, []);

  return (
    <div className="flex flex-col gap-y-3">
      <div className="flex min-h-[1.5rem] items-center justify-between">
        <PillToggle value={selectedType} onChange={updateResponseType}>
          <PillToggle.Button value={AiNodeV2ResponseFormatConfigTypeEnum.JSON}>
            Structured output
          </PillToggle.Button>
          <PillToggle.Button value={AiNodeV2ResponseFormatConfigTypeEnum.TEXT}>
            Text output
          </PillToggle.Button>
        </PillToggle>
        {selectedType === AiNodeV2ResponseFormatConfigTypeEnum.JSON &&
          !isEmpty && (
            <Button
              dataLoc="open-json-schema-editor"
              // Stays enabled when the flow is published so the schema can be
              // opened read-only; only disabled while the raw editor is locked.
              isDisabled={isLocked}
              size="sm"
              variant="secondary"
              onClick={handleOpenJsonSchemaEditor}
            >
              JSON view
            </Button>
          )}
      </div>

      {selectedType === AiNodeV2ResponseFormatConfigTypeEnum.JSON && (
        <>
          <div className="font-inter-normal-12px text-gray-500">
            Structure the AI's output as a JSON object to easily process and use
            the response in your Decision Flow. This ensures consistent,
            parsable outputs.
          </div>
          {responseSchemaErrors && responseSchemaErrors.length > 0 && (
            <Callout dataLoc="ai-node-response-schema-error" variant="error">
              <div className="flex flex-col gap-y-1">
                <span className="font-inter-medium-12px">
                  {responseSchemaErrors.length > 1
                    ? "The response schema couldn't be resolved:"
                    : "The response schema couldn't be resolved."}
                </span>
                {responseSchemaErrors.length > 1 ? (
                  <ul className="flex list-disc flex-col gap-y-1 pl-4">
                    {responseSchemaErrors.map((message, index) => (
                      <li key={`${index}-${message}`}>{message}</li>
                    ))}
                  </ul>
                ) : (
                  <span>{responseSchemaErrors[0]}</span>
                )}
              </div>
            </Callout>
          )}
          <JsonSchemaEditor
            immutable={immutable}
            rowsFormLocation={formLocationRows}
            schema={uiSchema?._schema as JsonSchema}
            typeOptions={SCHEMA_DEFAULT_TYPE_OPTIONS}
            includeDefsAsTypeOptions
            onClear={handleFieldsCleared}
            onOpenRawJsonView={handleOpenJsonSchemaEditor}
          />
        </>
      )}
      {selectedType === AiNodeV2ResponseFormatConfigTypeEnum.TEXT && (
        <TextOutputMapping
          immutable={immutable}
          runFieldErrors={runFieldErrors}
        />
      )}
      <Insights immutable={immutable} runFieldErrors={runFieldErrors} />
    </div>
  );
};
